import { useMemo } from 'react';
import { useUxpHostInfoContext } from '../contexts/UxpHostInfoContext';
import { isVersionGreaterOrEqual } from '../utils/isVersionGreaterOrEqual';

const FEATURE_SCHEMA_VERSIONS = {
  canManagePresets: '0.0.133',
  canSyncPlayhead: '0.0.136',
  canRemoveExportMetadata: '0.0.137',
  canHandleExportMetadataEvents: '0.0.137',
  /**
   * Host marker ↔ comment flows:
   * - `CommentMarker*` status APIs (vs legacy marker-sync status).
   * - `documentMarkers` query + client µs normalization (vs legacy `getMarkersFromSequence` +
   *   timeline-second `Marker.start`).
   * - Premiere `sendMarkers` includes `documentId` and export-metadata-based marker times
   *   ({@link usePremiereCommentMarkerSyncConfig}); legacy emits omit `documentId` and use
   *   `timestampMicroseconds` only.
   */
  canUseAgnosticCommentMarkerSync: '0.0.158',
  canUseHiddenMarkerUploadIds: '0.0.169',
} as const;

const FEATURE_PLUGIN_VERSIONS = {
  canUseZeroClickAuth: '1.0.24',
  canUseZCALegalCompliance: '1.0.27',
} as const;

type PluginFeatures = {
  [K in keyof typeof FEATURE_SCHEMA_VERSIONS]: boolean;
} & {
  [K in keyof typeof FEATURE_PLUGIN_VERSIONS]: boolean;
};

/**
 * Hook that returns feature flags based on the current plugin version.
 * Centralizes version checks for plugin capabilities.
 *
 * @returns Object with boolean flags for each feature
 *
 * @example
 * const { canManagePresets } = usePluginFeatures();
 */
export const usePluginFeatures = (): PluginFeatures => {
  const { pluginVersion, schemaVersion } = useUxpHostInfoContext();

  return useMemo(
    () => ({
      canManagePresets: isVersionGreaterOrEqual(
        schemaVersion,
        FEATURE_SCHEMA_VERSIONS.canManagePresets
      ),
      canSyncPlayhead: isVersionGreaterOrEqual(
        schemaVersion,
        FEATURE_SCHEMA_VERSIONS.canSyncPlayhead
      ),
      canRemoveExportMetadata: isVersionGreaterOrEqual(
        schemaVersion,
        FEATURE_SCHEMA_VERSIONS.canRemoveExportMetadata
      ),
      canHandleExportMetadataEvents: isVersionGreaterOrEqual(
        schemaVersion,
        FEATURE_SCHEMA_VERSIONS.canHandleExportMetadataEvents
      ),
      canUseAgnosticCommentMarkerSync: isVersionGreaterOrEqual(
        schemaVersion,
        FEATURE_SCHEMA_VERSIONS.canUseAgnosticCommentMarkerSync
      ),
      canUseZeroClickAuth: isVersionGreaterOrEqual(
        pluginVersion,
        FEATURE_PLUGIN_VERSIONS.canUseZeroClickAuth
      ),
      canUseZCALegalCompliance: isVersionGreaterOrEqual(
        pluginVersion,
        FEATURE_PLUGIN_VERSIONS.canUseZCALegalCompliance
      ),
      canUseHiddenMarkerUploadIds: isVersionGreaterOrEqual(
        schemaVersion,
        FEATURE_SCHEMA_VERSIONS.canUseHiddenMarkerUploadIds
      ),
    }),
    [pluginVersion, schemaVersion]
  );
};
